// Live's layer editor draft (spec G13b; live's `layer-editor-draft.ts`): the composition and the
// selection kept in this browser per viewer and job after each change, and restored when the
// editor opens on the same document. A draft that no longer fits (another document, more than
// live's 250 layers counted with the removed, a layer out of bounds) is dropped and the editor
// opens fresh. The undo history is not kept, as live's.
import { z } from "zod";

import type { SourceRef } from "../../../api/generated/model";
import { clearDraftSlot, readDraftSlot, writeDraftSlot } from "../../../platform/prefs";
import {
  MAX_SCALE,
  MIN_SCALE,
  type Composition,
  type EditorLayer,
  type LayerDocument,
  type PictureLayer,
} from "./model";
import { TEXT_FONT_IDS, validText } from "./text";

/** Live's cap on a draft's layers, the removed ones counted. */
export const DRAFT_LAYERS = 250;
const VERSION = 1;

const frame = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().positive(),
  height: z.number().positive(),
});
const ref = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("job_output"), job_id: z.uuid(), output_index: z.number().int().min(0) }),
  z.object({ kind: z.literal("asset"), asset_id: z.uuid() }),
]);
const picture = z.object({
  kind: z.literal("picture"),
  id: z.string().min(1),
  name: z.string(),
  frame,
  source: z.enum(["original", "generation", "library", "product", "edit"]),
  ref,
  url: z.string().min(1),
  width: z.number().positive(),
  height: z.number().positive(),
});
const text = z.object({
  kind: z.literal("text"),
  id: z.string().min(1),
  name: z.string(),
  frame,
  source: z.literal("text"),
  text: z.string().max(500),
  font: z.enum(TEXT_FONT_IDS),
  weight: z.enum(["regular", "bold"]),
  size: z.number().int().min(8).max(1024),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  align: z.enum(["left", "center", "right"]),
});
const transform = z.object({
  dx: z.number().finite(),
  dy: z.number().finite(),
  scale: z.number().min(MIN_SCALE).max(MAX_SCALE),
  removed: z.boolean(),
});
const draft = z.object({
  version: z.literal(VERSION),
  document: z.string(),
  composition: z.object({
    width: z.number().positive(),
    height: z.number().positive(),
    order: z.array(z.string()).max(DRAFT_LAYERS),
    layers: z.record(z.string(), z.discriminatedUnion("kind", [picture, text])),
    transforms: z.record(z.string(), transform),
  }),
  selected: z.string().nullable(),
});

export interface Draft {
  composition: Composition;
  selected: string | null;
}

/** Which document a draft belongs to: its layers' places, never their signed URLs. */
export function documentKey(document: LayerDocument): string {
  return JSON.stringify([
    document.width,
    document.height,
    document.layers.map((layer) => [layer.output_index, layer.z_index, layer.box, layer.width, layer.height]),
  ]);
}

/** A draft's key in prefs' `layerEditor` slot: the viewer's and the job's ids, as live's. */
export function draftKey(viewer: string, jobId: string): string {
  return `${viewer}:${jobId}`;
}

/** Every id once, in `order`, `layers` and `transforms` alike, and every text one of live's valid
 * ones (a weight its font has). */
function consistent(composition: Composition): boolean {
  const ids = composition.order;
  if (new Set(ids).size !== ids.length) return false;
  if (Object.values(composition.layers).some((layer) => layer.kind === "text" && !validText(layer))) return false;
  const layerIds = Object.keys(composition.layers);
  const transformIds = Object.keys(composition.transforms);
  return (
    layerIds.length === ids.length &&
    transformIds.length === ids.length &&
    ids.every((id) => composition.layers[id]?.id === id && composition.transforms[id] !== undefined)
  );
}

/**
 * The draft kept for this viewer, job and document, its original layers' pictures from the
 * document as now read (their signed URLs expire; its added pictures are read again by
 * `refreshAdded`); none when there is none or it no longer fits, in which case it is dropped.
 */
export function readDraft(viewer: string, jobId: string, document: LayerDocument): Draft | null {
  const key = draftKey(viewer, jobId);
  const parsed = readDraftSlot("layerEditor", key, draft);
  const drop = () => {
    clearDraftSlot("layerEditor", key);
    return null;
  };
  if (parsed === null || parsed.document !== documentKey(document)) return drop();
  const composition = parsed.composition as Composition;
  if (!consistent(composition)) return drop();
  // An original layer's picture as the document now signs it.
  const urls = new Map(document.layers.map((layer) => [`layer-${layer.z_index}`, layer.url]));
  const layers: Record<string, EditorLayer> = {};
  for (const [id, layer] of Object.entries(composition.layers)) {
    if (layer.kind !== "picture" || layer.source !== "original") {
      layers[id] = layer;
      continue;
    }
    const fresh = urls.get(id);
    if (fresh === undefined || fresh === null) return drop();
    layers[id] = { ...layer, url: fresh };
  }
  const restored = { ...composition, layers };
  const kept = parsed.selected;
  const selected =
    kept !== null && restored.layers[kept] !== undefined && !restored.transforms[kept]!.removed
      ? kept
      : null;
  return { composition: restored, selected };
}

/** A picture's signed URL read afresh from what it is; null when it is gone, a throw when the read failed. */
export type ReadPicture = (ref: SourceRef) => Promise<string | null>;

function addedPicture(layer: EditorLayer | undefined): layer is PictureLayer {
  return layer?.kind === "picture" && layer.source !== "original";
}

/** Whether a kept draft holds pictures added from the library, My Generations, a product or an AI edit. */
export function hasAddedPictures(value: Draft): boolean {
  return Object.values(value.composition.layers).some(addedPicture);
}

/**
 * A kept draft with its added pictures read again (G13b): a picture added from the library, My
 * Generations, a product or an AI edit is signed too and expires, so each is read again from its
 * `ref`, as the original layers are from the document. One that is gone (a file deleted, an
 * output no longer delivered) leaves the draft, with its place, its transform and the selection
 * when it was the selected one. A read that fails rejects the whole, so nothing is dropped for it.
 */
export async function refreshAdded(value: Draft, read: ReadPicture): Promise<Draft> {
  const { composition } = value;
  const ids = composition.order.filter((id) => addedPicture(composition.layers[id]));
  const urls = await Promise.all(ids.map((id) => read((composition.layers[id] as PictureLayer).ref)));
  const fresh = new Map(ids.map((id, at) => [id, urls[at] ?? null]));
  const gone = new Set(ids.filter((id) => fresh.get(id) === null));
  const layers: Record<string, EditorLayer> = {};
  const transforms: Composition["transforms"] = {};
  for (const id of composition.order) {
    if (gone.has(id)) continue;
    const layer = composition.layers[id]!;
    const url = fresh.get(id);
    layers[id] = url === undefined || url === null || layer.kind !== "picture" ? layer : { ...layer, url };
    transforms[id] = composition.transforms[id]!;
  }
  const order = composition.order.filter((id) => !gone.has(id));
  const selected = value.selected !== null && gone.has(value.selected) ? null : value.selected;
  return { composition: { ...composition, order, layers, transforms }, selected };
}

/** Keeps the draft after a change (never mid-gesture), as live's. */
export function writeDraft(
  viewer: string,
  jobId: string,
  document: LayerDocument,
  value: Draft,
): void {
  const kept = { version: VERSION, document: documentKey(document), ...value };
  writeDraftSlot("layerEditor", draftKey(viewer, jobId), kept);
}
